import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const ReunionesProfesor = () => {
  const { API, user } = useContext(AuthContext);
  const [reuniones, setReuniones] = useState([]);
  const [convocatorias, setConvocatorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingConvocatorias, setLoadingConvocatorias] = useState(true);
  const [activeTab, setActiveTab] = useState('convocatorias');
  
  // Estados para crear convocatoria (solo admin)
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [profesores, setProfesores] = useState([]);
  const [nuevaConvocatoria, setNuevaConvocatoria] = useState({
    titulo: '',
    descripcion: '',
    fecha_reunion: '',
    hora_reunion: '',
    duracion_minutos: 60,
    profesores_ids: [],
    crear_zoom: true,
    prioridad: 'media'
  });
  const [creando, setCreando] = useState(false);
  
  // Estados para modal de detalle
  const [selectedConvocatoria, setSelectedConvocatoria] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [comentarioText, setComentarioText] = useState('');
  const [comentarioFile, setComentarioFile] = useState(null);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  
  // Estado para respuestas
  const [showRespuestasModal, setShowRespuestasModal] = useState(null);
  const [respuestasDetalle, setRespuestasDetalle] = useState(null);
  
  // Estado para agregar profesores
  const [showAgregarProfesModal, setShowAgregarProfesModal] = useState(null);
  const [profesoresDisponibles, setProfesoresDisponibles] = useState([]);
  const [profesoresSeleccionados, setProfesoresSeleccionados] = useState([]);
  const [agregandoProfesores, setAgregandoProfesores] = useState(false);

  // Estados para consultas
  const [consultas, setConsultas] = useState([]);
  const [loadingConsultas, setLoadingConsultas] = useState(true);
  const [showCrearConsultaModal, setShowCrearConsultaModal] = useState(false);
  const [nuevaConsulta, setNuevaConsulta] = useState({
    titulo: '',
    opciones: ['', ''],
    fecha_limite: '',
    hora_limite: '',
    profesores_ids: []
  });
  const [creandoConsulta, setCreandoConsulta] = useState(false);

  useEffect(() => {
    cargarReuniones();
    cargarConvocatorias();
    cargarConsultas();
    // Cargar profesores para consultas (admin y profesores pueden crear)
    if (user?.rol === 'admin' || user?.rol === 'profesor') {
      cargarProfesores();
    }
  }, []);

  const cargarReuniones = async () => {
    try {
      const response = await axios.get(`${API}/reuniones-profesores`);
      setReuniones(response.data.reuniones || []);
    } catch (error) {
      console.error('Error cargando reuniones:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarConvocatorias = async () => {
    try {
      setLoadingConvocatorias(true);
      const endpoint = user?.rol === 'admin' 
        ? `${API}/admin/convocatorias-reunion`
        : `${API}/convocatorias-reunion`;
      const response = await axios.get(endpoint);
      setConvocatorias(response.data.convocatorias || []);
    } catch (error) {
      console.error('Error cargando convocatorias:', error);
    } finally {
      setLoadingConvocatorias(false);
    }
  };

  const cargarConsultas = async () => {
    try {
      setLoadingConsultas(true);
      const response = await axios.get(`${API}/consultas-profesores`);
      setConsultas(response.data.consultas || []);
    } catch (error) {
      console.error('Error cargando consultas:', error);
    } finally {
      setLoadingConsultas(false);
    }
  };

  const crearConsulta = async () => {
    if (!nuevaConsulta.titulo.trim()) {
      toast.error('Ingresa un título para la consulta');
      return;
    }
    
    const opcionesValidas = nuevaConsulta.opciones.filter(o => o.trim());
    if (opcionesValidas.length < 2) {
      toast.error('Debes agregar al menos 2 opciones');
      return;
    }
    
    if (!nuevaConsulta.fecha_limite || !nuevaConsulta.hora_limite) {
      toast.error('Ingresa fecha y hora límite');
      return;
    }
    
    if (nuevaConsulta.profesores_ids.length === 0) {
      toast.error('Debes seleccionar al menos un profesor');
      return;
    }
    
    try {
      setCreandoConsulta(true);
      const fechaLimite = new Date(`${nuevaConsulta.fecha_limite}T${nuevaConsulta.hora_limite}`);
      
      await axios.post(`${API}/consultas-profesores`, {
        titulo: nuevaConsulta.titulo,
        opciones: opcionesValidas,
        fecha_limite: fechaLimite.toISOString(),
        profesores_ids: nuevaConsulta.profesores_ids
      });
      
      toast.success('✅ Consulta creada exitosamente');
      setShowCrearConsultaModal(false);
      setNuevaConsulta({ titulo: '', opciones: ['', ''], fecha_limite: '', hora_limite: '', profesores_ids: [] });
      cargarConsultas();
    } catch (error) {
      console.error('Error creando consulta:', error);
      toast.error(error.response?.data?.detail || 'Error al crear consulta');
    } finally {
      setCreandoConsulta(false);
    }
  };

  const votarConsulta = async (consultaId, opcionIndex) => {
    try {
      // Verificar si ya votó para mostrar mensaje apropiado
      const consulta = consultas.find(c => c.id === consultaId);
      const yaVoto = consulta?.mi_voto !== undefined && consulta?.mi_voto !== null;
      
      await axios.post(`${API}/consultas-profesores/${consultaId}/votar`, {
        opcion_index: opcionIndex
      });
      toast.success(yaVoto ? '🔄 Voto actualizado' : '✅ Voto registrado');
      cargarConsultas();
    } catch (error) {
      console.error('Error votando:', error);
      toast.error(error.response?.data?.detail || 'Error al votar');
    }
  };

  const cerrarConsulta = async (consultaId) => {
    try {
      await axios.put(`${API}/consultas-profesores/${consultaId}/cerrar`);
      toast.success('Consulta cerrada');
      cargarConsultas();
    } catch (error) {
      console.error('Error cerrando consulta:', error);
      toast.error('Error al cerrar consulta');
    }
  };

  const eliminarConsulta = async (consultaId) => {
    if (!window.confirm('¿Eliminar esta consulta?')) return;
    try {
      await axios.delete(`${API}/consultas-profesores/${consultaId}`);
      toast.success('Consulta eliminada');
      cargarConsultas();
    } catch (error) {
      console.error('Error eliminando consulta:', error);
      toast.error('Error al eliminar consulta');
    }
  };

  const agregarOpcion = () => {
    if (nuevaConsulta.opciones.length < 6) {
      setNuevaConsulta({ ...nuevaConsulta, opciones: [...nuevaConsulta.opciones, ''] });
    }
  };

  const eliminarOpcion = (index) => {
    if (nuevaConsulta.opciones.length > 2) {
      const nuevasOpciones = nuevaConsulta.opciones.filter((_, i) => i !== index);
      setNuevaConsulta({ ...nuevaConsulta, opciones: nuevasOpciones });
    }
  };

  const cargarProfesores = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filtrar solo profesores activos (no estudiantes ni otros roles)
      const soloProfeores = response.data.filter(u => 
        u.activo !== false && 
        (u.rol === 'profesor' || u.rol === 'admin')
      );
      setProfesores(soloProfeores);
    } catch (error) {
      console.error('Error cargando profesores:', error);
    }
  };

  const cargarDetalleConvocatoria = async (convocatoriaId) => {
    try {
      const response = await axios.get(`${API}/convocatorias-reunion/${convocatoriaId}`);
      setSelectedConvocatoria(response.data);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast.error('Error al cargar detalles');
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
    }
    if (url.includes('drive.google.com')) {
      const fileId = url.match(/[-\w]{25,}/);
      return fileId ? `https://drive.google.com/file/d/${fileId[0]}/preview` : null;
    }
    return url;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Montevideo'
    });
  };

  const formatFechaCorta = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Montevideo'
    });
  };

  const responderConvocatoria = async (convocatoriaId, respuesta) => {
    try {
      await axios.post(`${API}/convocatorias-reunion/${convocatoriaId}/responder?respuesta=${respuesta}`);
      toast.success(respuesta === 'presente' ? '✅ Has confirmado tu asistencia' : '❌ Has indicado que no podrás asistir');
      
      // Actualizar estado local del modal inmediatamente si está abierto
      if (selectedConvocatoria?.id === convocatoriaId) {
        setSelectedConvocatoria(prev => ({
          ...prev,
          mi_respuesta: respuesta
        }));
      }
      
      // Recargar datos
      cargarConvocatorias();
      
      // Recargar detalle del modal si está abierto (para actualizar stats)
      if (selectedConvocatoria?.id === convocatoriaId) {
        setTimeout(() => cargarDetalleConvocatoria(convocatoriaId), 300);
      }
    } catch (error) {
      toast.error('Error al registrar respuesta');
      console.error(error);
    }
  };

  const crearConvocatoria = async () => {
    if (!nuevaConvocatoria.titulo || !nuevaConvocatoria.fecha_reunion || !nuevaConvocatoria.hora_reunion) {
      toast.error('Completa título, fecha y hora');
      return;
    }
    if (nuevaConvocatoria.profesores_ids.length === 0) {
      toast.error('Selecciona al menos un profesor');
      return;
    }

    setCreando(true);
    try {
      const fechaCompleta = `${nuevaConvocatoria.fecha_reunion}T${nuevaConvocatoria.hora_reunion}:00`;
      
      const response = await axios.post(`${API}/admin/convocatorias-reunion`, {
        titulo: nuevaConvocatoria.titulo,
        descripcion: nuevaConvocatoria.descripcion,
        fecha_reunion: fechaCompleta,
        duracion_minutos: nuevaConvocatoria.duracion_minutos,
        profesores_ids: nuevaConvocatoria.profesores_ids,
        crear_zoom: nuevaConvocatoria.crear_zoom,
        prioridad: nuevaConvocatoria.prioridad
      });

      if (response.data.success) {
        toast.success(response.data.zoom_creado 
          ? '✅ Convocatoria creada con reunión de Zoom' 
          : '✅ Convocatoria creada');
        setShowCrearModal(false);
        setNuevaConvocatoria({
          titulo: '',
          descripcion: '',
          fecha_reunion: '',
          hora_reunion: '',
          duracion_minutos: 60,
          profesores_ids: [],
          crear_zoom: true,
          prioridad: 'media'
        });
        cargarConvocatorias();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear convocatoria');
    } finally {
      setCreando(false);
    }
  };

  const eliminarConvocatoria = async (id) => {
    if (!window.confirm('¿Eliminar esta convocatoria?')) return;
    try {
      await axios.delete(`${API}/admin/convocatorias-reunion/${id}`);
      toast.success('Convocatoria eliminada');
      cargarConvocatorias();
      setIsDetailModalOpen(false);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const verRespuestas = async (convocatoriaId) => {
    try {
      const response = await axios.get(`${API}/admin/convocatorias-reunion/${convocatoriaId}/respuestas`);
      setRespuestasDetalle(response.data);
      setIsDetailModalOpen(false); // Cerrar modal de detalle primero
      setTimeout(() => {
        setShowRespuestasModal(convocatoriaId);
      }, 100);
    } catch (error) {
      toast.error('Error al cargar respuestas');
    }
  };

  const abrirAgregarProfesores = async (convocatoriaId) => {
    try {
      const response = await axios.get(`${API}/admin/convocatorias-reunion/${convocatoriaId}/profesores-disponibles`);
      setProfesoresDisponibles(response.data.profesores || []);
      setProfesoresSeleccionados([]);
      setIsDetailModalOpen(false);
      setTimeout(() => {
        setShowAgregarProfesModal(convocatoriaId);
      }, 100);
    } catch (error) {
      toast.error('Error al cargar profesores disponibles');
    }
  };

  const agregarProfesoresSeleccionados = async () => {
    if (profesoresSeleccionados.length === 0) {
      toast.error('Selecciona al menos un profesor');
      return;
    }

    setAgregandoProfesores(true);
    try {
      const response = await axios.post(
        `${API}/admin/convocatorias-reunion/${showAgregarProfesModal}/agregar-profesores`,
        {
          profesores_ids: profesoresSeleccionados,
          enviar_notificacion: true
        }
      );

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}. Se enviaron ${response.data.notificaciones_enviadas} notificaciones.`);
        setShowAgregarProfesModal(null);
        setProfesoresSeleccionados([]);
        cargarConvocatorias();
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar profesores');
    } finally {
      setAgregandoProfesores(false);
    }
  };

  const toggleProfesorDisponible = (profId) => {
    setProfesoresSeleccionados(prev => 
      prev.includes(profId)
        ? prev.filter(id => id !== profId)
        : [...prev, profId]
    );
  };

  const agregarComentario = async (e) => {
    e.preventDefault();
    if (!comentarioText.trim()) {
      toast.error('Escribe un comentario');
      return;
    }

    setEnviandoComentario(true);
    try {
      let response;
      if (comentarioFile) {
        const formData = new FormData();
        formData.append('texto', comentarioText);
        formData.append('file', comentarioFile);
        response = await axios.post(
          `${API}/convocatorias-reunion/${selectedConvocatoria.id}/comentarios/adjunto`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        response = await axios.post(
          `${API}/convocatorias-reunion/${selectedConvocatoria.id}/comentarios`,
          { texto: comentarioText }
        );
      }

      setSelectedConvocatoria(response.data);
      setComentarioText('');
      setComentarioFile(null);
      const fileInput = document.getElementById('comentario-file-input');
      if (fileInput) fileInput.value = '';
      toast.success(comentarioFile ? '💬📎 Comentario con archivo agregado' : '💬 Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const toggleProfesor = (profId) => {
    setNuevaConvocatoria(prev => ({
      ...prev,
      profesores_ids: prev.profesores_ids.includes(profId)
        ? prev.profesores_ids.filter(id => id !== profId)
        : [...prev.profesores_ids, profId]
    }));
  };

  const seleccionarTodos = () => {
    setNuevaConvocatoria(prev => ({
      ...prev,
      profesores_ids: profesores.map(p => p.id)
    }));
  };

  const getEstadoConvocatoria = (conv) => {
    const fecha = new Date(conv.fecha_reunion);
    const ahora = new Date();
    const diffHoras = (fecha - ahora) / (1000 * 60 * 60);
    
    if (diffHoras < -2) return { texto: 'Finalizada', color: 'bg-gray-500', columna: 'finalizada' };
    if (diffHoras < 0) return { texto: 'En curso', color: 'bg-green-500 animate-pulse', columna: 'en_curso' };
    if (diffHoras < 1) return { texto: '¡Próximamente!', color: 'bg-yellow-500', columna: 'programada' };
    if (diffHoras < 24) return { texto: 'Hoy', color: 'bg-blue-500', columna: 'programada' };
    return { texto: 'Programada', color: 'bg-indigo-500', columna: 'programada' };
  };

  const getPrioridadInfo = (prioridad) => {
    switch (prioridad) {
      case 'urgente': return { color: 'bg-red-600', texto: '🔴 Urgente', gradient: 'from-red-500 to-red-700' };
      case 'alta': return { color: 'bg-orange-500', texto: '🟠 Alta', gradient: 'from-orange-500 to-red-500' };
      case 'media': return { color: 'bg-yellow-500', texto: '🟡 Media', gradient: 'from-yellow-500 to-orange-500' };
      case 'baja': return { color: 'bg-green-500', texto: '🟢 Baja', gradient: 'from-green-500 to-emerald-500' };
      default: return { color: 'bg-gray-500', texto: 'Normal', gradient: 'from-gray-500 to-gray-600' };
    }
  };

  // Agrupar convocatorias por estado para vista Kanban
  const convocatoriasPorEstado = {
    programada: convocatorias.filter(c => {
      const estado = getEstadoConvocatoria(c);
      return estado.columna === 'programada';
    }),
    en_curso: convocatorias.filter(c => {
      const estado = getEstadoConvocatoria(c);
      return estado.columna === 'en_curso';
    }),
    finalizada: convocatorias.filter(c => {
      const estado = getEstadoConvocatoria(c);
      return estado.columna === 'finalizada';
    })
  };

  // Renderizar tarjeta de convocatoria
  const renderConvocatoriaCard = (conv) => {
    const estado = getEstadoConvocatoria(conv);
    const prioridad = getPrioridadInfo(conv.prioridad || 'media');
    const esPendiente = conv.mi_respuesta === 'pendiente';

    return (
      <Card 
        key={conv.id} 
        className={`cursor-pointer hover:shadow-lg transition-all border-l-4 ${
          conv.prioridad === 'urgente' ? 'border-l-red-500' :
          conv.prioridad === 'alta' ? 'border-l-orange-500' :
          conv.prioridad === 'media' ? 'border-l-yellow-500' :
          'border-l-green-500'
        }`}
        onClick={() => cargarDetalleConvocatoria(conv.id)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-gray-800 flex-1 pr-2 line-clamp-2">
              {conv.titulo}
            </h4>
            <Badge className={`${estado.color} text-white text-xs flex-shrink-0`}>
              {estado.texto}
            </Badge>
          </div>
          
          <div className="space-y-2 text-sm text-gray-600">
            <p className="flex items-center gap-2">
              <i className="fas fa-calendar text-indigo-500"></i>
              {formatFechaCorta(conv.fecha_reunion)}
            </p>
            
            {conv.link_zoom && (
              <p className="flex items-center gap-2 text-blue-600">
                <i className="fas fa-video"></i>
                Zoom disponible
              </p>
            )}

            <div className="flex items-center gap-2">
              <Badge className={`${prioridad.color} text-white text-xs`}>
                {prioridad.texto}
              </Badge>
              {conv.comentarios?.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  <i className="fas fa-comments mr-1"></i>
                  {conv.comentarios.length}
                </Badge>
              )}
            </div>
          </div>

          {/* Estado de respuesta */}
          {esPendiente ? (
            <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-700 font-medium mb-2">
                <i className="fas fa-exclamation-circle mr-1"></i>
                Confirma tu asistencia
              </p>
              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <Button
                  size="sm"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-xs"
                  onClick={() => responderConvocatoria(conv.id, 'presente')}
                >
                  <i className="fas fa-check mr-1"></i> Asistiré
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 text-xs"
                  onClick={() => responderConvocatoria(conv.id, 'ausente')}
                >
                  <i className="fas fa-times mr-1"></i> No puedo
                </Button>
              </div>
            </div>
          ) : (
            <div className={`mt-3 p-2 rounded-lg text-xs ${
              conv.mi_respuesta === 'presente' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {conv.mi_respuesta === 'presente' ? (
                <><i className="fas fa-check-circle mr-1"></i> Asistencia confirmada</>
              ) : (
                <><i className="fas fa-times-circle mr-1"></i> No asistirás</>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <i className="fas fa-users text-indigo-600"></i>
          Reuniones de Profesores
        </h1>
        <p className="text-gray-600">Convocatorias, comunicación y grabaciones de reuniones</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="convocatorias" className="flex items-center gap-2">
            <i className="fas fa-calendar-check"></i>
            <span className="hidden sm:inline">Convocatorias</span>
            <span className="sm:hidden">Conv.</span>
            {convocatorias.filter(c => c.mi_respuesta === 'pendiente').length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {convocatorias.filter(c => c.mi_respuesta === 'pendiente').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="consultas" className="flex items-center gap-2">
            <i className="fas fa-poll"></i>
            <span className="hidden sm:inline">Consultas</span>
            <span className="sm:hidden">Cons.</span>
            {consultas.filter(c => c.estado === 'activa' && c.mi_voto === undefined).length > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {consultas.filter(c => c.estado === 'activa' && c.mi_voto === undefined).length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <i className="fas fa-video"></i>
            <span className="hidden sm:inline">Videos</span>
            <span className="sm:hidden">Vid.</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB: CONVOCATORIAS */}
        <TabsContent value="convocatorias">
          {/* Botón crear (solo admin) */}
          {user?.rol === 'admin' && (
            <div className="mb-6">
              <Button 
                onClick={() => setShowCrearModal(true)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg"
              >
                <i className="fas fa-plus mr-2"></i>
                Nueva Convocatoria
              </Button>
            </div>
          )}

          {loadingConvocatorias ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando convocatorias...</p>
            </div>
          ) : convocatorias.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <i className="fas fa-calendar-times text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-500">No hay convocatorias</p>
              </CardContent>
            </Card>
          ) : (
            /* Vista Kanban */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Columna Programadas */}
              <div>
                <div className="bg-indigo-50 border-2 border-indigo-200 rounded-t-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-indigo-800 flex items-center gap-2">
                      <i className="fas fa-calendar-alt"></i>
                      Programadas
                    </h3>
                    <Badge className="bg-indigo-600 text-white">
                      {convocatoriasPorEstado.programada.length}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  {convocatoriasPorEstado.programada.length === 0 ? (
                    <Card className="bg-gray-50">
                      <CardContent className="p-6 text-center text-gray-500">
                        <i className="fas fa-calendar-check text-3xl mb-2"></i>
                        <p className="text-sm">No hay reuniones programadas</p>
                      </CardContent>
                    </Card>
                  ) : (
                    convocatoriasPorEstado.programada.map(renderConvocatoriaCard)
                  )}
                </div>
              </div>

              {/* Columna En Curso */}
              <div>
                <div className="bg-green-50 border-2 border-green-200 rounded-t-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-green-800 flex items-center gap-2">
                      <i className="fas fa-video animate-pulse"></i>
                      En Curso
                    </h3>
                    <Badge className="bg-green-600 text-white">
                      {convocatoriasPorEstado.en_curso.length}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  {convocatoriasPorEstado.en_curso.length === 0 ? (
                    <Card className="bg-gray-50">
                      <CardContent className="p-6 text-center text-gray-500">
                        <i className="fas fa-broadcast-tower text-3xl mb-2"></i>
                        <p className="text-sm">No hay reuniones en curso</p>
                      </CardContent>
                    </Card>
                  ) : (
                    convocatoriasPorEstado.en_curso.map(renderConvocatoriaCard)
                  )}
                </div>
              </div>

              {/* Columna Finalizadas */}
              <div>
                <div className="bg-gray-100 border-2 border-gray-200 rounded-t-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-700 flex items-center gap-2">
                      <i className="fas fa-check-circle"></i>
                      Finalizadas
                    </h3>
                    <Badge className="bg-gray-600 text-white">
                      {convocatoriasPorEstado.finalizada.length}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {convocatoriasPorEstado.finalizada.length === 0 ? (
                    <Card className="bg-gray-50">
                      <CardContent className="p-6 text-center text-gray-500">
                        <i className="fas fa-history text-3xl mb-2"></i>
                        <p className="text-sm">No hay reuniones finalizadas</p>
                      </CardContent>
                    </Card>
                  ) : (
                    convocatoriasPorEstado.finalizada.slice(0, 10).map(renderConvocatoriaCard)
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* TAB: CONSULTAS */}
        <TabsContent value="consultas">
          {/* Botón crear consulta */}
          <div className="mb-6">
            <Button 
              onClick={() => setShowCrearConsultaModal(true)}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg"
            >
              <i className="fas fa-poll mr-2"></i>
              Nueva Consulta
            </Button>
          </div>

          {loadingConsultas ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando consultas...</p>
            </div>
          ) : consultas.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <i className="fas fa-poll text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-500">No hay consultas activas</p>
                <p className="text-sm text-gray-400 mt-2">Crea una consulta para tomar decisiones en equipo</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {consultas.map((consulta) => {
                const esActiva = consulta.estado === 'activa';
                const yaVote = consulta.mi_voto !== undefined && consulta.mi_voto !== null;
                const fechaLimite = new Date(consulta.fecha_limite);
                const esCreador = consulta.creador_id === user?.id;
                
                return (
                  <Card 
                    key={consulta.id} 
                    className={`overflow-hidden ${
                      !esActiva ? 'opacity-75 bg-gray-50' : ''
                    }`}
                  >
                    <CardHeader className={`pb-2 ${
                      esActiva ? 'bg-gradient-to-r from-orange-50 to-amber-50' : 'bg-gray-100'
                    }`}>
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <i className={`fas fa-poll ${esActiva ? 'text-orange-500' : 'text-gray-400'}`}></i>
                            {consulta.titulo}
                          </CardTitle>
                          <p className="text-sm text-gray-500 mt-1">
                            <i className="fas fa-user mr-1"></i>
                            {consulta.creador_nombre}
                            <span className="mx-2">•</span>
                            <i className="fas fa-clock mr-1"></i>
                            {esActiva ? 'Vence:' : 'Venció:'} {fechaLimite.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Montevideo' })} {fechaLimite.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })}
                          </p>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Badge className={
                            consulta.estado === 'activa' ? 'bg-green-500 text-white' :
                            consulta.estado === 'vencida' ? 'bg-red-500 text-white' :
                            'bg-gray-500 text-white'
                          }>
                            {consulta.estado === 'activa' ? '🟢 Activa' :
                             consulta.estado === 'vencida' ? '🔴 Vencida' : '⚫ Cerrada'}
                          </Badge>
                          {(esCreador || user?.rol === 'admin') && esActiva && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cerrarConsulta(consulta.id)}
                              className="text-xs"
                            >
                              <i className="fas fa-lock mr-1"></i>
                              Cerrar
                            </Button>
                          )}
                          {(esCreador || user?.rol === 'admin') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => eliminarConsulta(consulta.id)}
                              className="text-xs text-red-600 hover:bg-red-50"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {/* Resultados con gráfica */}
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm text-gray-500 mb-2">
                          <span><i className="fas fa-users mr-1"></i> {consulta.total_votos} votos</span>
                          {yaVote && <span className="text-green-600"><i className="fas fa-check-circle mr-1"></i> Ya votaste (puedes cambiar)</span>}
                        </div>
                        
                        {consulta.resultados?.map((resultado, idx) => {
                          const esGanador = consulta.total_votos > 0 && 
                            resultado.votos === Math.max(...consulta.resultados.map(r => r.votos)) &&
                            resultado.votos > 0;
                          const esMiVoto = consulta.mi_voto === idx;
                          
                          return (
                            <div key={idx} className="relative">
                              <div 
                                className={`p-3 rounded-lg border-2 transition-all ${
                                  esMiVoto ? 'border-orange-400 bg-orange-50' :
                                  esGanador && !esActiva ? 'border-green-400 bg-green-50' :
                                  'border-gray-200 hover:border-gray-300'
                                } ${esActiva ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                onClick={() => esActiva && votarConsulta(consulta.id, idx)}
                              >
                                <div className="flex justify-between items-center mb-2">
                                  <span className={`font-medium ${esGanador && !esActiva ? 'text-green-700' : ''}`}>
                                    {esMiVoto && <i className="fas fa-check-circle text-orange-500 mr-2"></i>}
                                    {esGanador && !esActiva && <i className="fas fa-trophy text-yellow-500 mr-2"></i>}
                                    {resultado.opcion}
                                  </span>
                                  <span className="text-sm font-bold">
                                    {resultado.porcentaje}% ({resultado.votos})
                                  </span>
                                </div>
                                
                                {/* Barra de progreso */}
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all duration-500 ${
                                      esGanador && !esActiva ? 'bg-green-500' :
                                      esMiVoto ? 'bg-orange-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${resultado.porcentaje}%` }}
                                  ></div>
                                </div>
                                
                                {/* Votantes (transparente) */}
                                {resultado.votantes?.length > 0 && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    <i className="fas fa-users mr-1"></i>
                                    {resultado.votantes.map(v => v.nombre).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {esActiva && !yaVote && (
                          <p className="text-center text-sm text-orange-600 mt-3">
                            <i className="fas fa-hand-pointer mr-1"></i>
                            Click en una opción para votar
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* TAB: VIDEOS */}
        <TabsContent value="videos">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : reuniones.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <i className="fas fa-film text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-500">No hay videos de reuniones disponibles</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reuniones.map((reunion) => (
                <Card key={reunion.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                  <CardContent className="p-0">
                    {(reunion.video_url || reunion.link_video) ? (
                      <div className="aspect-video relative">
                        {getVideoEmbedUrl(reunion.video_url || reunion.link_video) ? (
                          <iframe
                            src={getVideoEmbedUrl(reunion.video_url || reunion.link_video)}
                            className="w-full h-full"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            title={reunion.titulo}
                          />
                        ) : (
                          <a 
                            href={reunion.video_url || reunion.link_video} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex flex-col items-center justify-center text-white hover:from-indigo-600 hover:to-purple-700 transition-all cursor-pointer"
                          >
                            <i className="fas fa-play-circle text-5xl mb-2"></i>
                            <span className="text-sm font-medium">Ver Video</span>
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-100 flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          <i className="fas fa-video-slash text-4xl mb-2"></i>
                          <p className="text-sm">Sin video</p>
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 mb-2 line-clamp-1">{reunion.titulo}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">{reunion.descripcion}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="fas fa-calendar"></i>
                          {formatFechaCorta(reunion.fecha)}
                        </p>
                        {(reunion.video_url || reunion.link_video) && (
                          <a 
                            href={reunion.video_url || reunion.link_video}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            <i className="fas fa-external-link-alt mr-1"></i>
                            Abrir
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal: Crear Convocatoria */}
      {showCrearModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-calendar-plus text-indigo-600"></i>
                Nueva Convocatoria a Reunión
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título de la reunión *
                </label>
                <input
                  type="text"
                  value={nuevaConvocatoria.titulo}
                  onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, titulo: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ej: Reunión mensual de profesores"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción / Agenda
                </label>
                <textarea
                  value={nuevaConvocatoria.descripcion}
                  onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, descripcion: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Temas a tratar en la reunión..."
                />
              </div>

              {/* Fecha, Hora y Prioridad */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    value={nuevaConvocatoria.fecha_reunion}
                    onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, fecha_reunion: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora *
                  </label>
                  <input
                    type="time"
                    value={nuevaConvocatoria.hora_reunion}
                    onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, hora_reunion: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridad
                  </label>
                  <select
                    value={nuevaConvocatoria.prioridad}
                    onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, prioridad: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="baja">🟢 Baja</option>
                    <option value="media">🟡 Media</option>
                    <option value="alta">🟠 Alta</option>
                    <option value="urgente">🔴 Urgente</option>
                  </select>
                </div>
              </div>

              {/* Duración */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración (minutos)
                </label>
                <select
                  value={nuevaConvocatoria.duracion_minutos}
                  onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, duracion_minutos: parseInt(e.target.value)})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value={30}>30 minutos</option>
                  <option value={60}>1 hora</option>
                  <option value={90}>1 hora 30 min</option>
                  <option value={120}>2 horas</option>
                </select>
              </div>

              {/* Crear Zoom */}
              <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="crear_zoom"
                  checked={nuevaConvocatoria.crear_zoom}
                  onChange={(e) => setNuevaConvocatoria({...nuevaConvocatoria, crear_zoom: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="crear_zoom" className="text-sm text-gray-700">
                  <i className="fas fa-video text-blue-600 mr-1"></i>
                  Crear reunión de Zoom automáticamente
                </label>
              </div>

              {/* Seleccionar Profesores */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Profesores a convocar * ({nuevaConvocatoria.profesores_ids.length} seleccionados)
                  </label>
                  <button
                    type="button"
                    onClick={seleccionarTodos}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Seleccionar todos
                  </button>
                </div>
                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                  {profesores.map((prof) => (
                    <label
                      key={prof.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        nuevaConvocatoria.profesores_ids.includes(prof.id) ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={nuevaConvocatoria.profesores_ids.includes(prof.id)}
                        onChange={() => toggleProfesor(prof.id)}
                        className="w-4 h-4"
                      />
                      <span>{prof.nombre} {prof.apellido}</span>
                      <span className="text-xs text-gray-400 ml-auto">{prof.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <Button
                variant="outline"
                onClick={() => setShowCrearModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={crearConvocatoria}
                disabled={creando}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {creando ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Crear y Notificar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de Convocatoria */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="!left-[50%] !translate-x-[-50%] w-[92vw] sm:w-[88vw] md:w-[80vw] lg:w-[850px] max-h-[90vh] p-0 overflow-hidden rounded-xl">
          {selectedConvocatoria && (
            <div className="flex flex-col h-full max-h-[90vh] overflow-x-hidden">
              {/* Header con gradiente según prioridad */}
              <div 
                className="px-6 py-4 border-b"
                style={{
                  background: selectedConvocatoria.prioridad === 'urgente' 
                    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    : selectedConvocatoria.prioridad === 'alta'
                    ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                    : selectedConvocatoria.prioridad === 'media'
                    ? 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'
                    : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                }}
              >
                <DialogTitle className="text-white text-xl font-bold mb-2">
                  {selectedConvocatoria.titulo}
                </DialogTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-white/20 text-white border-white/40 backdrop-blur-sm">
                    <i className="fas fa-star mr-1"></i>
                    {getPrioridadInfo(selectedConvocatoria.prioridad || 'media').texto}
                  </Badge>
                  <Badge className="bg-white/20 text-white border-white/40 backdrop-blur-sm">
                    <i className="fas fa-calendar mr-1"></i>
                    {formatFechaCorta(selectedConvocatoria.fecha_reunion)}
                  </Badge>
                  <Badge className={`${getEstadoConvocatoria(selectedConvocatoria).color} text-white`}>
                    {getEstadoConvocatoria(selectedConvocatoria).texto}
                  </Badge>
                </div>
              </div>

              {/* Contenido scrolleable */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4">
                {/* Info principal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Descripción */}
                  <Card className="border-2">
                    <CardHeader className="py-3 bg-gray-50">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <i className="fas fa-align-left text-gray-600"></i>
                        Descripción / Agenda
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedConvocatoria.descripcion || 'Sin descripción'}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Link de Zoom */}
                  {selectedConvocatoria.link_zoom && (
                    <Card className="border-2 border-blue-200">
                      <CardHeader className="py-3 bg-blue-50">
                        <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                          <i className="fas fa-video"></i>
                          Enlace de Zoom
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <a
                          href={selectedConvocatoria.link_zoom}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-blue-600 hover:bg-blue-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                        >
                          <i className="fas fa-video mr-2"></i>
                          Unirse a la Reunión
                        </a>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Se abrirá en una nueva pestaña
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Estadísticas de respuestas */}
                {selectedConvocatoria.stats && (
                  <Card className="border-2">
                    <CardHeader className="py-3 bg-gray-50">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <i className="fas fa-chart-pie text-purple-600"></i>
                        Respuestas de Asistencia
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-4 gap-3">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">
                            {selectedConvocatoria.stats.total_convocados}
                          </div>
                          <div className="text-xs text-gray-500">Convocados</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {selectedConvocatoria.stats.confirmados_presente}
                          </div>
                          <div className="text-xs text-green-700">Presentes</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {selectedConvocatoria.stats.confirmados_ausente}
                          </div>
                          <div className="text-xs text-red-700">Ausentes</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">
                            {selectedConvocatoria.stats.pendientes}
                          </div>
                          <div className="text-xs text-yellow-700">Pendientes</div>
                        </div>
                      </div>
                      
                      {user?.rol === 'admin' && (
                        <div className="space-y-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => verRespuestas(selectedConvocatoria.id)}
                          >
                            <i className="fas fa-list mr-2"></i>
                            Ver Detalle de Respuestas
                          </Button>
                          <Button
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => abrirAgregarProfesores(selectedConvocatoria.id)}
                          >
                            <i className="fas fa-user-plus mr-2"></i>
                            Añadir Profesores
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Sección de Comentarios */}
                <Card className="border-2 border-indigo-200">
                  <CardHeader className="py-3 bg-indigo-50">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <i className="fas fa-comments text-indigo-600"></i>
                        Comentarios y Comunicación
                      </span>
                      <Badge className="bg-indigo-600 text-white">
                        {selectedConvocatoria.comentarios?.length || 0}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {/* Lista de comentarios */}
                    <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                      {selectedConvocatoria.comentarios && selectedConvocatoria.comentarios.length > 0 ? (
                        selectedConvocatoria.comentarios.map((comentario, index) => (
                          <div 
                            key={comentario.id || index} 
                            className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                  {comentario.usuario_nombre?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div>
                                  <span className="font-semibold text-sm text-gray-800">
                                    {comentario.usuario_nombre}
                                  </span>
                                  <p className="text-xs text-gray-500">
                                    <i className="fas fa-clock mr-1"></i>
                                    {new Date(comentario.fecha).toLocaleDateString('es-ES', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZone: 'America/Montevideo'
                                    })}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                #{index + 1}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700 ml-10 leading-relaxed">
                              {comentario.texto}
                            </p>
                            
                            {/* Archivo adjunto */}
                            {comentario.archivo_url && (
                              <div className="ml-10 mt-3">
                                <a
                                  href={comentario.archivo_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={comentario.archivo_nombre}
                                  className="inline-flex items-center gap-2 bg-white rounded-lg p-3 border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                                >
                                  <i className="fas fa-file-download text-blue-600"></i>
                                  <span className="text-sm text-gray-700">
                                    {comentario.archivo_nombre || 'Archivo adjunto'}
                                  </span>
                                </a>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <i className="fas fa-comments text-4xl text-gray-300 mb-2"></i>
                          <p className="text-sm text-gray-500">No hay comentarios aún</p>
                          <p className="text-xs text-gray-400 mt-1">Sé el primero en comentar</p>
                        </div>
                      )}
                    </div>

                    {/* Formulario de nuevo comentario */}
                    <form onSubmit={agregarComentario} className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-700">
                        <i className="fas fa-pen mr-1"></i>
                        Agregar Comentario
                      </Label>
                      <div className="flex gap-2">
                        <Textarea
                          value={comentarioText}
                          onChange={(e) => setComentarioText(e.target.value)}
                          placeholder="Escribe tu comentario, pregunta o aporte..."
                          rows={3}
                          className="flex-1 resize-none border-2 border-indigo-200 focus:border-indigo-500"
                        />
                        <Button 
                          type="submit" 
                          size="sm" 
                          disabled={enviandoComentario}
                          className="bg-indigo-600 hover:bg-indigo-700 h-auto px-4"
                        >
                          {enviandoComentario ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-paper-plane"></i>
                          )}
                        </Button>
                      </div>
                      
                      {/* Input de archivo adjunto */}
                      <div className="bg-blue-50 rounded-lg p-3 border-2 border-blue-200">
                        <Label className="text-xs font-semibold text-blue-800 mb-2 block">
                          <i className="fas fa-paperclip mr-1"></i>
                          Adjuntar Archivo (Opcional)
                        </Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            onChange={(e) => setComentarioFile(e.target.files[0])}
                            className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                            id="comentario-file-input"
                          />
                          {comentarioFile && (
                            <button
                              type="button"
                              onClick={() => {
                                setComentarioFile(null);
                                const input = document.getElementById('comentario-file-input');
                                if (input) input.value = '';
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="fas fa-times-circle"></i>
                            </button>
                          )}
                        </div>
                        {comentarioFile && (
                          <div className="mt-2 text-xs text-blue-700 bg-blue-100 rounded p-2">
                            <i className="fas fa-file mr-1"></i>
                            <strong>Seleccionado:</strong> {comentarioFile.name}
                          </div>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Footer con acciones */}
              <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Mostrar estado actual y opción de cambiar si ya respondió */}
                  {selectedConvocatoria.mi_respuesta !== 'pendiente' && (
                    <div className="flex-1 flex flex-col gap-2">
                      <div className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg ${
                        selectedConvocatoria.mi_respuesta === 'presente' 
                          ? 'bg-green-100 text-green-700 border border-green-300' 
                          : 'bg-red-100 text-red-700 border border-red-300'
                      }`}>
                        <i className={`fas ${selectedConvocatoria.mi_respuesta === 'presente' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                        <span className="font-medium">
                          {selectedConvocatoria.mi_respuesta === 'presente' ? 'Asistencia confirmada' : 'No asistirás a esta reunión'}
                        </span>
                      </div>
                      {/* Botón para cambiar respuesta */}
                      <Button
                        variant="outline"
                        size="sm"
                        className={selectedConvocatoria.mi_respuesta === 'presente' 
                          ? 'border-red-300 text-red-600 hover:bg-red-50' 
                          : 'border-green-300 text-green-600 hover:bg-green-50'}
                        onClick={() => responderConvocatoria(
                          selectedConvocatoria.id, 
                          selectedConvocatoria.mi_respuesta === 'presente' ? 'ausente' : 'presente'
                        )}
                      >
                        <i className={`fas ${selectedConvocatoria.mi_respuesta === 'presente' ? 'fa-times' : 'fa-check'} mr-2`}></i>
                        {selectedConvocatoria.mi_respuesta === 'presente' 
                          ? 'Cambiar a No Asistiré' 
                          : 'Cambiar a Sí Asistiré'}
                      </Button>
                    </div>
                  )}
                  
                  {/* Botones de respuesta si está pendiente */}
                  {selectedConvocatoria.mi_respuesta === 'pendiente' && (
                    <>
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => responderConvocatoria(selectedConvocatoria.id, 'presente')}
                      >
                        <i className="fas fa-check mr-2"></i>
                        Confirmar Asistencia
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => responderConvocatoria(selectedConvocatoria.id, 'ausente')}
                      >
                        <i className="fas fa-times mr-2"></i>
                        No Puedo Asistir
                      </Button>
                    </>
                  )}
                  
                  {/* Botón eliminar (solo admin) */}
                  {user?.rol === 'admin' && (
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => eliminarConvocatoria(selectedConvocatoria.id)}
                    >
                      <i className="fas fa-trash mr-2"></i>
                      Eliminar
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => setIsDetailModalOpen(false)}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Ver Respuestas (Admin) */}
      {showRespuestasModal && respuestasDetalle && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => { setShowRespuestasModal(null); setRespuestasDetalle(null); }}
        >
          <div 
            className="bg-white rounded-xl w-full max-w-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón X para cerrar */}
            <button
              type="button"
              onClick={() => { setShowRespuestasModal(null); setRespuestasDetalle(null); }}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
              style={{ zIndex: 10000 }}
            >
              <i className="fas fa-times"></i>
            </button>
            <div className="p-6 border-b pr-12">
              <h2 className="text-xl font-bold">Respuestas: {respuestasDetalle.titulo}</h2>
              <p className="text-sm text-gray-500">{formatFechaCorta(respuestasDetalle.fecha_reunion)}</p>
            </div>
            
            <div className="p-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{respuestasDetalle.stats.presente}</div>
                  <div className="text-xs text-green-700">Presentes</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{respuestasDetalle.stats.ausente}</div>
                  <div className="text-xs text-red-700">Ausentes</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{respuestasDetalle.stats.pendiente}</div>
                  <div className="text-xs text-yellow-700">Pendientes</div>
                </div>
              </div>

              {/* Lista de respuestas */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {respuestasDetalle.respuestas.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2 border rounded">
                    <span>{r.nombre}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      r.respuesta === 'presente' ? 'bg-green-100 text-green-700' :
                      r.respuesta === 'ausente' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {r.respuesta === 'presente' ? '✅ Presente' :
                       r.respuesta === 'ausente' ? '❌ Ausente' :
                       '⏳ Pendiente'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t">
              <button
                type="button"
                onClick={() => { 
                  setShowRespuestasModal(null); 
                  setRespuestasDetalle(null); 
                }}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors cursor-pointer"
                style={{ zIndex: 10000, position: 'relative' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Agregar Profesores */}
      {showAgregarProfesModal && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => { setShowAgregarProfesModal(null); setProfesoresSeleccionados([]); }}
        >
          <div 
            className="bg-white rounded-xl w-full max-w-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón X para cerrar */}
            <button
              type="button"
              onClick={() => { setShowAgregarProfesModal(null); setProfesoresSeleccionados([]); }}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="p-6 border-b pr-12">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-user-plus text-green-600"></i>
                Añadir Profesores
              </h2>
              <p className="text-sm text-gray-500">Selecciona los profesores a invitar</p>
            </div>
            
            <div className="p-6">
              {profesoresDisponibles.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-check-circle text-4xl text-green-500 mb-3"></i>
                  <p className="text-gray-600">Todos los profesores ya están invitados</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    {profesoresSeleccionados.length} de {profesoresDisponibles.length} seleccionados
                  </p>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {profesoresDisponibles.map((prof) => (
                      <label
                        key={prof.id}
                        className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                          profesoresSeleccionados.includes(prof.id) ? 'bg-green-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={profesoresSeleccionados.includes(prof.id)}
                          onChange={() => toggleProfesorDisponible(prof.id)}
                          className="w-4 h-4 text-green-600"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{prof.nombre} {prof.apellido}</p>
                          <p className="text-xs text-gray-500">{prof.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <Button
                    onClick={() => setProfesoresSeleccionados(profesoresDisponibles.map(p => p.id))}
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                  >
                    Seleccionar todos
                  </Button>
                </>
              )}
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                type="button"
                onClick={() => { setShowAgregarProfesModal(null); setProfesoresSeleccionados([]); }}
                className="flex-1 py-2 px-4 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              {profesoresDisponibles.length > 0 && (
                <button
                  type="button"
                  onClick={agregarProfesoresSeleccionados}
                  disabled={agregandoProfesores || profesoresSeleccionados.length === 0}
                  className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {agregandoProfesores ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </span>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Invitar ({profesoresSeleccionados.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Consulta */}
      {showCrearConsultaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
            {/* Botón X para cerrar */}
            <button
              type="button"
              onClick={() => { setShowCrearConsultaModal(false); setNuevaConsulta({ titulo: '', opciones: ['', ''], fecha_limite: '', hora_limite: '', profesores_ids: [] }); }}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors z-10"
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-amber-50">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-poll text-orange-500"></i>
                Nueva Consulta
              </h2>
              <p className="text-sm text-gray-500">Crea una consulta para toma de decisiones</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Título */}
              <div>
                <Label htmlFor="consulta-titulo" className="text-sm font-medium">
                  Pregunta o título de la consulta *
                </Label>
                <input
                  id="consulta-titulo"
                  type="text"
                  value={nuevaConsulta.titulo}
                  onChange={(e) => setNuevaConsulta({ ...nuevaConsulta, titulo: e.target.value })}
                  placeholder="Ej: ¿Cuándo realizamos el retiro?"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              {/* Opciones */}
              <div>
                <Label className="text-sm font-medium">
                  Opciones (mínimo 2, máximo 6) *
                </Label>
                <div className="mt-2 space-y-2">
                  {nuevaConsulta.opciones.map((opcion, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <span className="w-6 h-6 flex items-center justify-center bg-orange-100 text-orange-600 rounded-full text-sm font-medium">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={opcion}
                        onChange={(e) => {
                          const nuevasOpciones = [...nuevaConsulta.opciones];
                          nuevasOpciones[idx] = e.target.value;
                          setNuevaConsulta({ ...nuevaConsulta, opciones: nuevasOpciones });
                        }}
                        placeholder={`Opción ${idx + 1}`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      {nuevaConsulta.opciones.length > 2 && (
                        <button
                          type="button"
                          onClick={() => eliminarOpcion(idx)}
                          className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {nuevaConsulta.opciones.length < 6 && (
                  <button
                    type="button"
                    onClick={agregarOpcion}
                    className="mt-2 text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  >
                    <i className="fas fa-plus"></i>
                    Agregar opción
                  </button>
                )}
              </div>
              
              {/* Selección de profesores */}
              <div>
                <Label className="text-sm font-medium">
                  Profesores a consultar *
                </Label>
                <div className="mt-2 flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setNuevaConsulta({ 
                      ...nuevaConsulta, 
                      profesores_ids: profesores.map(p => p.id) 
                    })}
                    className="text-xs text-orange-600 hover:text-orange-700"
                  >
                    <i className="fas fa-check-double mr-1"></i>
                    Seleccionar todos
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => setNuevaConsulta({ ...nuevaConsulta, profesores_ids: [] })}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    <i className="fas fa-times mr-1"></i>
                    Limpiar
                  </button>
                </div>
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {profesores.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500 text-center">Cargando profesores...</p>
                  ) : (
                    profesores.map((prof) => (
                      <label
                        key={prof.id}
                        className={`flex items-center gap-3 p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                          nuevaConsulta.profesores_ids.includes(prof.id) ? 'bg-orange-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={nuevaConsulta.profesores_ids.includes(prof.id)}
                          onChange={() => {
                            const ids = nuevaConsulta.profesores_ids.includes(prof.id)
                              ? nuevaConsulta.profesores_ids.filter(id => id !== prof.id)
                              : [...nuevaConsulta.profesores_ids, prof.id];
                            setNuevaConsulta({ ...nuevaConsulta, profesores_ids: ids });
                          }}
                          className="w-4 h-4 text-orange-500 rounded"
                        />
                        <div className="flex-1 text-sm">
                          <span className="font-medium">{prof.nombre} {prof.apellido}</span>
                          <span className="text-gray-400 ml-2 text-xs">{prof.email}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {nuevaConsulta.profesores_ids.length} profesor(es) seleccionado(s)
                </p>
              </div>
              
              {/* Fecha y hora límite */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="consulta-fecha" className="text-sm font-medium">
                    Fecha límite *
                  </Label>
                  <input
                    id="consulta-fecha"
                    type="date"
                    value={nuevaConsulta.fecha_limite}
                    onChange={(e) => setNuevaConsulta({ ...nuevaConsulta, fecha_limite: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="consulta-hora" className="text-sm font-medium">
                    Hora límite *
                  </Label>
                  <input
                    id="consulta-hora"
                    type="time"
                    value={nuevaConsulta.hora_limite}
                    onChange={(e) => setNuevaConsulta({ ...nuevaConsulta, hora_limite: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
              
              <p className="text-xs text-gray-500">
                <i className="fas fa-info-circle mr-1"></i>
                Las respuestas son transparentes: todos pueden ver quién votó cada opción.
                Se enviará notificación a los profesores al crear y al vencer.
              </p>
            </div>
            
            {/* Botones */}
            <div className="p-4 border-t bg-gray-50 flex gap-3">
              <button
                type="button"
                onClick={() => { setShowCrearConsultaModal(false); setNuevaConsulta({ titulo: '', opciones: ['', ''], fecha_limite: '', hora_limite: '', profesores_ids: [] }); }}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={crearConsulta}
                disabled={creandoConsulta}
                className="flex-1 py-2 px-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creandoConsulta ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creando...
                  </span>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Crear y Notificar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReunionesProfesor;
